import { Box, Flex } from '@radix-ui/themes';
import { useMemo } from 'react';
import { useVideoJs } from '../hooks';
import { AdvancedVideoEngineProps } from '../types';

import React from 'react';
import 'video.js/dist/video-js.css';
import { createPlayerObserver } from '../utils/playerObserver';
import styles from './VideoJs.module.css';

export const VideoJsEngine = ({
    src,
    media,
    onReady,
    tracks: _,
}: AdvancedVideoEngineProps) => {
    const observer = React.useMemo(() => createPlayerObserver(), []);

    const videoJsOptions = useMemo(
        () => ({
            autoplay: false,
            controls: true,
            muted: true,
            responsive: true,
            fluid: true,
            sources: [{ src, type: media.mimeType }],

            crossOrigin: 'anonymous',

            playbackRates: [0.5, 1, 1.5, 2],

            // audioOnlyMode: true,
            // audioPosterMode: true,

            controlBar: {
                volumePanel: {
                    // inline: false,
                },

                children: [
                    'playToggle',
                    'volumePanel',
                    // 'currentTimeDisplay',
                    // 'timeDivider',
                    // 'durationDisplay',
                    'progressControl',
                    'playbackRateMenuButton',
                    'fullscreenToggle',
                ],
            },
        }),
        [media.mimeType, src],
    );

    const { videoContainerRef, player } = useVideoJs(videoJsOptions);

    React.useEffect(() => {
        if (player) {
            onReady({
                play: () => player.play(),
                pause: () => player.pause(),
                seek: (time) => player.currentTime(time),

                seekRelativeFrames: (frames) => {
                    const currentTime = player.currentTime() || 0;
                    player.currentTime(
                        currentTime + frames * (1 / media.frameRate.numerator),
                    );
                },

                getCurrentTime: () => player.currentTime() || 0,

                togglePlayPause: function (): void {
                    throw new Error('Function not implemented.');
                },
                faster: function (): void {
                    throw new Error('Function not implemented.');
                },
                slower: function (): void {
                    throw new Error('Function not implemented.');
                },
                seekRelative: function (_time: number): void {
                    throw new Error('Function not implemented.');
                },
                seekFrames: function (_time: number): void {
                    throw new Error('Function not implemented.');
                },
                nextFrame: function (): void {
                    throw new Error('Function not implemented.');
                },
                previousFrame: function (): void {
                    throw new Error('Function not implemented.');
                },
                playlistPrevious: function (): void {
                    throw new Error('Function not implemented.');
                },
                playlistNext: function (): void {
                    throw new Error('Function not implemented.');
                },
                subscribe: observer.subscribe,
                osdShow: function (
                    _message: string,
                    _durationMs?: number,
                ): void {
                    throw new Error('Function not implemented.');
                },
                osdHide: function (_message?: string): void {
                    throw new Error('Function not implemented.');
                },
                timecodeShow: function (): void {
                    throw new Error('Function not implemented.');
                },
                timecodeHide: function (): void {
                    throw new Error('Function not implemented.');
                },
                timecodeToggle: function (): void {
                    throw new Error('Function not implemented.');
                },
            });
        }
    }, [media.frameRate.numerator, observer.subscribe, onReady, player]);

    return (
        <Flex direction='row' className={styles.engineWrapper}>
            <Box flexGrow='1' position='relative'>
                <div data-vjs-player>
                    <div
                        ref={videoContainerRef}
                        className={styles.playerInstance}
                    />
                </div>
            </Box>
        </Flex>
    );
};
